/**
 * The offline change queue's wire format (issue #64, step 2).
 *
 * It lives here rather than in `apps/web` for the same reason the API client
 * does: React Native adopts it unchanged, and a queue whose types were defined
 * inside the web app would be a second queue the moment a second surface needed
 * one.
 *
 * Two shapes carry the whole design:
 *
 *   - **A record is named by a ref, not an id.** MySQL assigns ids and a device
 *     in a cellar has no way to ask for one, so a record created offline is
 *     named by a *client* id until sync hands back the real one. Everything
 *     that points at a record — a later edit, an item's location, a lantern's
 *     fixture model — points through a `RecordRef`, so an item created into a
 *     location created five minutes earlier resolves once both have landed.
 *
 *   - **An edit carries what it was, per field.** Not a version number per
 *     record: a counter tells you *something* changed, and the walker being
 *     asked to answer a conflict needs to know *which field*, or the question
 *     is unanswerable. Two people editing different fields of the same item is
 *     then a merge rather than a question, which is what makes most conflicts
 *     evaporate.
 */

import type { EquipmentCategory, WorkingState } from './types'

/**
 * How long a change may sit on a device before sync refuses it. Set by the
 * project owner, like the audit log's six months — a fortnight covers a holiday
 * or a dark period between productions, and still refuses a count somebody took
 * before the last get-out.
 *
 * Not an environment variable, for the same reason the audit log's retention is
 * not one: a container quietly disagreeing with what the app tells the walker
 * would be worse than no limit at all.
 */
export const SYNC_MAX_AGE_DAYS = 14

/**
 * What a photograph is reduced to before the device stores it (issue #64,
 * step 4) — the same 600px JPEG the server keeps, and for the same reasons
 * (`ImageProcessor`).
 *
 * Mirrored here rather than guessed at, because a device producing anything
 * else would be storing a size nothing ever displays. The server re-encodes
 * what it is sent regardless; this is what stops a walk's worth of photographs
 * being a hundred and fifty megabytes on a phone.
 */
export const PHOTO_EDGE = 600
export const PHOTO_TYPE = 'image/jpeg'
export const PHOTO_QUALITY = 0.82

/**
 * How many photographs may wait on the device at once.
 *
 * At 600px they are seventy kilobytes or so each, so this is a bound of about
 * eight megabytes — comfortably inside what a browser gives a site, and enough
 * for a walk of the whole store with a picture of everything that looked wrong.
 * An unbounded queue is how a device fills its disk and starts refusing writes
 * in a way nobody standing in a cellar can diagnose.
 */
export const QUEUED_PHOTO_MAX = 100

/**
 * How many photographs the device keeps a copy of for reading offline
 * (issue #70).
 *
 * A different number from the one above and a different question: that one
 * bounds what is *waiting to be sent*, where the cost of losing one is somebody
 * having to walk back to a shelf, and this one bounds what has been brought
 * *down* to look at, where the cost of shedding one is a picture that fills in
 * again next time there is a signal. So it can be much larger.
 *
 * At the ~70KB a 600px JPEG comes to, three hundred is about twenty megabytes —
 * comfortably inside what a browser gives a site, and more photographs than the
 * theatre has kit with one. The bound exists because an unbounded one is how a
 * phone fills its disk and starts refusing writes in a way nobody standing in a
 * cellar can diagnose. Set by the project owner, like the queue's fourteen days.
 */
export const PHOTO_CACHE_MAX = 300

/**
 * The entities a change may be queued against.
 *
 * Split in two because they are shaped differently and the split is what keeps
 * a nonsense changeset unrepresentable. A **record** is created, edited, binned
 * and restored, and every one of those is a set of fields with base values. A
 * **walk** is none of those things: it is started, counted, and finished or
 * abandoned, and "delete a stocktake" is not an operation this app has. Naming
 * the two separately means the union below simply cannot express one.
 */
export type RecordEntity = 'item' | 'location' | 'fixtureModel'

/** Stocktakes joined in step 3; photographs are step 4. */
export type SyncEntity = RecordEntity | 'stocktake'

/**
 * Names a record the queue is about.
 *
 * `clientId` is what a record created offline is called until it has a real id.
 * The server keeps the mapping, so a changeset that arrives in a *later* batch
 * — a retry after a dropped connection, an edit made the next morning — still
 * resolves against the row the create actually produced.
 */
export type RecordRef = { readonly id: number } | { readonly clientId: string }

export function isClientRef(ref: RecordRef): ref is { readonly clientId: string } {
  return 'clientId' in ref
}

/**
 * A client id for a record that does not exist yet. Prefixed so it can never be
 * mistaken for a server id in a log or a URL, and random enough that two
 * devices walking the same building do not collide.
 */
export function newClientId(): string {
  return `new_${crypto.randomUUID()}`
}

/**
 * What a field was, and what it becomes.
 *
 * `base` is what the field read on the device when the walker changed it, and
 * is what the server compares against. A field whose base still matches is
 * applied; one whose base has moved is the conflict the resolver asks about.
 *
 * `unknown` rather than a per-field union because the fields differ by entity
 * and the server is the thing that types them — `App\Http\Payload` turns
 * everything off the wire into a known type or a 400, and it does that for the
 * sync endpoint exactly as it does for a PATCH.
 */
export interface FieldEdit {
  readonly base: unknown
  readonly value: unknown
}

/**
 * The fields an edit touched, keyed by name.
 *
 * An object and not a list, because **absent and null have to stay tellable
 * apart** (issue #64). `null` on `lamp` means "clear this override and go back
 * to reading the fixture model" (issue #59); the key being missing means the
 * edit never touched the lamp at all. A format that dropped nulls could not say
 * the first, and one that sent every field would turn every edit into a
 * whole-record conflict.
 */
export type FieldEdits = Readonly<Record<string, FieldEdit>>

/** The same, for a create — nothing existed, so there is no base to carry. */
export type FieldValues = Readonly<Record<string, unknown>>

interface ChangesetBase {
  /**
   * Client-generated and unique-indexed server-side. A request that succeeded
   * but whose answer was lost on a flaky connection must not apply twice, so a
   * repeat is answered with the original result rather than re-run — otherwise
   * a reconnect in a stairwell creates the item again.
   */
  readonly id: string
  readonly entity: SyncEntity
  /**
   * When the walker made the change, not when it reached the server. The audit
   * trail records this as when it happened, because a safety record that
   * attributed a Tuesday count to Thursday's reconnection would be quietly
   * false.
   */
  readonly at: string
}

/**
 * A record made on the device. `clientId` is what later changesets in the queue
 * point at, and what the server maps to the row it creates.
 */
export interface CreateChangeset extends ChangesetBase {
  readonly kind: 'create'
  readonly entity: RecordEntity
  readonly clientId: string
  readonly fields: FieldValues
}

export interface UpdateChangeset extends ChangesetBase {
  readonly kind: 'update'
  readonly entity: RecordEntity
  readonly target: RecordRef
  readonly fields: FieldEdits
  /**
   * Fields the walker has already answered a conflict about, re-sent with their
   * bases moved to what the server said. Named so the trail can record that a
   * conflict was resolved rather than that a value happened to match.
   */
  readonly resolved?: readonly string[]
}

/**
 * Binning and restoring (issues #8, #9). Both are soft and both put the record
 * back whole, which is what makes them pleasantly replayable — there is no base
 * value to compare, because there is no field being set.
 *
 * Purge and empty-trash are deliberately absent: they are admin-only and
 * destructive, and there is no hurry to destroy a record from a cellar.
 */
export interface DeleteChangeset extends ChangesetBase {
  readonly kind: 'delete'
  readonly entity: RecordEntity
  readonly target: RecordRef
}

export interface RestoreChangeset extends ChangesetBase {
  readonly kind: 'restore'
  readonly entity: RecordEntity
  readonly target: RecordRef
}

/**
 * The walking order (issue #27), which is one whole-order `PUT` and must never
 * be replayed as N moves.
 *
 * A queued reorder supersedes any earlier queued reorder — the client replaces
 * rather than appends — and on replay it is last-writer-wins with no conflict
 * question. An order is the sort of thing where asking is worse than telling:
 * nobody can answer "your order or theirs" field by field, and the person who
 * dragged it last is the person who meant it.
 */
export interface ReorderChangeset extends ChangesetBase {
  readonly kind: 'reorder'
  readonly entity: 'location'
  readonly order: readonly RecordRef[]
}

// — stocktakes (issue #64, step 3) —

/**
 * A walk started on the device.
 *
 * The locations are refs rather than ids for the same reason everything else
 * is: somebody may have made the shelf that morning with no signal, and a walk
 * that could not include it would be a walk of the building as it was
 * yesterday.
 *
 * **The lines are not carried.** A walk of the whole store is hundreds of them,
 * and the server rebuilds the set from the items it holds in those places —
 * which is the truth about what *should* be there. What the device knows and
 * the server cannot is what was actually counted, and every count carries its
 * own item ref, so a count always lands whether or not the rebuilt set expected
 * that item. That is the answer to the line-set drift the issue names: kit
 * somebody else added mid-walk is simply an uncounted line, and kit that moved
 * out from under the walker still takes their count.
 */
export interface StartWalkChangeset extends ChangesetBase {
  readonly kind: 'create'
  readonly entity: 'stocktake'
  readonly clientId: string
  readonly locations: readonly RecordRef[]
}

/**
 * One line's count and note.
 *
 * **A line is named by its item, not by its line id.** Line ids are assigned by
 * MySQL at `start()`, so a walk begun in a cellar has none — and the pairing is
 * unique anyway (`uniq_stocktake_item`), which is what makes the item the
 * record's real name. It is the same rule as the rest of the queue, one level
 * down: a thing is named by something the device can know.
 */
export interface CountChangeset extends ChangesetBase {
  readonly kind: 'count'
  readonly entity: 'stocktake'
  readonly target: RecordRef
  readonly item: RecordRef
  /**
   * What the sheet in the walker's hand said the shelf should hold. Used only
   * when the replay has to *create* the line — it never overwrites one the
   * server fixed at `start()`, because that one is already the truth about the
   * walk. Where a line has to be invented, this is truer than the item's
   * quantity now, which may have moved since the walker looked at it.
   */
  readonly expectedQuantity: number
  readonly fields: FieldEdits
}

export interface CompleteWalkChangeset extends ChangesetBase {
  readonly kind: 'complete'
  readonly entity: 'stocktake'
  readonly target: RecordRef
  /** Whether the counts are written back onto the inventory (spec W-04). */
  readonly applyCounts: boolean
}

export interface AbandonWalkChangeset extends ChangesetBase {
  readonly kind: 'abandon'
  readonly entity: 'stocktake'
  readonly target: RecordRef
}

// — photographs (issue #64, step 4) —

/**
 * The one thing this app keeps that is not JSON, so the one change that is
 * queued with everything else and **sent on its own**.
 *
 * `POST /api/sync` takes a JSON batch, and bytes are not JSON: base64 in the
 * body would be a third again as large in memory on both sides, and it would
 * put a five-megabyte string in the middle of a request whose whole job is to
 * be small and survivable. So a photograph replays through the image endpoints
 * that already exist (issue #56), one request each, **after** the batch.
 *
 * That order is not a preference. An item created with no connection has no id
 * until the batch answers with one, so its photograph could not go first even
 * if it wanted to.
 *
 * **One queue, two transports.** It stays in the same queue as everything else
 * because everything else about it is the same: it is made in order, it counts
 * towards the depth the shell shows, it is discarded by the same button, and it
 * meets the same fourteen days. The server never sees this `kind` — it is not
 * in `ChangesetReader::KINDS`, so one arriving at `/api/sync` is refused at the
 * door.
 *
 * There is **no base value and no conflict**. A photograph is not a field two
 * people can disagree about the way they disagree about a quantity: "your
 * picture or theirs" is a question with no answer anybody could give. Last
 * writer wins, for the reason a reorder does.
 */
export interface PhotographChangeset extends ChangesetBase {
  readonly kind: 'photograph'
  readonly entity: 'item' | 'fixtureModel'
  readonly target: RecordRef
  /**
   * `set` has its bytes stored on the device under this changeset's id;
   * `clear` removes the record's *own* photograph, which on an item means
   * dropping back to its fixture model's rather than reaching through to it.
   */
  readonly action: 'set' | 'clear'
}

export type Changeset =
  | CreateChangeset
  | UpdateChangeset
  | DeleteChangeset
  | RestoreChangeset
  | ReorderChangeset
  | StartWalkChangeset
  | CountChangeset
  | CompleteWalkChangeset
  | AbandonWalkChangeset
  | PhotographChangeset

export interface SyncRequest {
  /** In the order they were made. The server applies them in that order. */
  readonly changesets: readonly Changeset[]
}

/**
 * One field the device and the server disagree about.
 *
 * `theirs` is what the server holds now; `mine` is what this device wants;
 * `base` is what the device thought it was changing from, which is the third
 * value the walker needs to make sense of the other two. Who moved it and when
 * come from the audit log, which already holds exactly that.
 */
export interface ConflictField {
  readonly field: string
  /** The label the audit log and the forms already use for this field. */
  readonly label: string
  readonly base: string | null
  readonly mine: string | null
  readonly theirs: string | null
  readonly changedBy: string | null
  readonly changedAt: string | null
}

/**
 * What became of one changeset.
 *
 * Only `applied` is remembered. A conflict and a block are both "not yet" —
 * the changeset stays in the queue and is sent again once it has been answered
 * or its dependency has landed — and a rejection is not an application either:
 * "create the Ladder Store" refused today because a binned location is holding
 * the name must be able to succeed tomorrow once somebody has emptied the
 * trash. Recording any of the three would turn a temporary answer into a
 * permanent one.
 */
export type SyncStatus = 'applied' | 'conflicted' | 'rejected' | 'blocked'

/**
 * A rejection the resolver has more to offer about than "read this and decide"
 * (issue #64, step 3).
 *
 * Almost every rejection is a sentence: a binned location is holding the name,
 * the item has been destroyed. There is nothing to do but read it.
 *
 * `walk_already_open` is the exception, because it is the one refusal that is
 * **temporary and will lift by itself**. Only one stocktake may be in progress
 * (spec W-04), and a walk begun in a cellar can meet one somebody else started
 * meanwhile — but they will finish, and then this walk applies untouched. So
 * the resolver offers to *keep* it rather than only to read about it, and the
 * app unblocks in the meantime.
 *
 * Branching on the code and never on the prose is the same rule the sign-in
 * screen follows with `?authError=`. A client that does not recognise a code
 * falls back to presenting the reason, which is always set alongside it.
 */
export type SyncRejectionCode = 'walk_already_open'

export interface SyncResult {
  readonly changesetId: string
  readonly status: SyncStatus
  /** The row a create produced, so the client can retire its client id. */
  readonly recordId?: number
  readonly clientId?: string
  /** Set on `conflicted`, one entry per field whose base had moved. */
  readonly conflicts?: readonly ConflictField[]
  /** Set on `rejected` and on `blocked` — a sentence for the walker. */
  readonly reason?: string
  /** Set on the few rejections the walker can answer rather than only read. */
  readonly code?: SyncRejectionCode
  /**
   * Set on `blocked`: the changeset this one is waiting for. An item created
   * into a location whose own create has not landed cannot be applied, and
   * applying it anywhere else would be a guess.
   */
  readonly blockedBy?: string
  /** How the record now reads, for `applied` — so the caches can be corrected. */
  readonly record?: unknown
}

export interface SyncResponse {
  readonly results: readonly SyncResult[]
}

/**
 * Whether a change has sat unsynced for longer than the theatre allows.
 *
 * Checked on the client so the walker is told before a round trip, and again on
 * the server, which is the one that decides — a device's clock is the device's.
 */
export function isStale(at: string, now: Date = new Date()): boolean {
  const made = Date.parse(at)

  // An unreadable timestamp is not evidence of staleness; the server refuses it
  // as malformed, which is a different sentence and a truer one.
  if (Number.isNaN(made)) return false

  return now.getTime() - made > SYNC_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
}

/**
 * The fields a changeset may carry, per entity.
 *
 * Named here rather than left to whatever a form happens to send, because this
 * is the list the server will accept and the client must not be able to queue
 * something that will be rejected on arrival for a reason nobody can act on.
 * Photographs are absent on purpose — holding originals on a phone is step 4.
 */
export const SYNCABLE_FIELDS: Readonly<Record<SyncEntity, readonly string[]>> = {
  item: [
    'category',
    'name',
    'brand',
    'notes',
    'quantity',
    'locationId',
    'madsCode',
    'patTestCode',
    'patTestDate',
    'workingState',
    'fixtureModelId',
    'lightType',
    'lamp',
    'watts',
    'gelFrame',
    'goboFrame',
    'barnDoor',
  ],
  location: ['name', 'description'],
  fixtureModel: [
    'name',
    'brand',
    'lightType',
    'lamp',
    'watts',
    'gelFrame',
    'goboFrame',
    'barnDoor',
    'notes',
  ],
  // A count and the note beside it are the whole of what a walk records
  // (spec W-04). Everything else about a stocktake — which places, when it
  // started, whether it is over — is said by the kind of changeset rather than
  // by a field, because none of those is something anybody edits twice.
  stocktake: ['countedQuantity', 'note'],
}

/**
 * A record created offline lands on `Not tested`, exactly as one created online
 * does (issue #59) — the queue must not assert `Working` on the walker's
 * behalf for kit nobody has checked. Stated here so the provisional record the
 * device shows says the same thing the server will.
 */
export const OFFLINE_NEW_WORKING_STATE: WorkingState = 'Not tested'

/** Categories that carry a working state at all, mirroring the server's enum. */
export function isElectricalCategory(category: EquipmentCategory): boolean {
  return category !== 'Cabling'
}
